'use client'

import { CheckCircle, Sparkles, Star } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

const CLOSE_DELAY = 200
const MODAL_SEEN_KEY = 'new-platform-modal-seen'

interface NewPlatformModalProps {
  isOpen: boolean
  onClose: () => void
}

export function NewPlatformModal({ isOpen, onClose }: NewPlatformModalProps) {
  const [shouldShow, setShouldShow] = useState(false)

  useEffect(() => {
    const hasSeenModal = localStorage.getItem(MODAL_SEEN_KEY)
    if (!hasSeenModal) {
      setShouldShow(true)
    }
  }, [])

  const handleClose = () => {
    localStorage.setItem(MODAL_SEEN_KEY, 'true')
    setTimeout(() => {
      onClose()
    }, CLOSE_DELAY)
  }

  if (!shouldShow) {
    return null
  }

  return (
    <Dialog onOpenChange={handleClose} open={isOpen}>
      <DialogContent className="max-w-[90vw] sm:max-w-md">
        <DialogHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-primary-200 via-primary-500 to-primary-700">
            <Sparkles className="h-8 w-8 text-white" />
          </div>
          <DialogTitle className="text-center font-bold text-2xl text-gray-900">
            Bem-vindo à Nova Plataforma!
          </DialogTitle>
          <DialogDescription className="text-center text-base text-gray-600">
            Estamos muito felizes em ter você nesta nova jornada
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
              <div>
                <h4 className="font-semibold text-gray-900">Cara Nova</h4>
                <p className="text-gray-600 text-sm">
                  Interface completamente redesenhada para uma experiência mais
                  moderna e intuitiva
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Star className="mt-0.5 h-5 w-5 flex-shrink-0 text-yellow-500" />
              <div>
                <h4 className="font-semibold text-gray-900">
                  Recursos Aprimorados
                </h4>
                <p className="text-gray-600 text-sm">
                  Novas funcionalidades e melhorias para otimizar seu
                  aprendizado
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
              <div>
                <h4 className="font-semibold text-gray-900">
                  Performance Superior
                </h4>
                <p className="text-gray-600 text-sm">
                  Plataforma mais rápida e responsiva para uma navegação fluida
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-lg bg-primary-50 p-4">
            <p className="text-primary-800 text-sm">
              <strong>Dica:</strong> Explore todas as funcionalidades para
              aproveitar ao máximo sua experiência!
            </p>
          </div>
        </div>

        <div className="flex justify-center">
          <Button
            className="bg-primary-600 px-8 py-2 text-white hover:bg-primary-700"
            onClick={handleClose}
          >
            Entendi, obrigado!
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
